export interface PublishedProfile {
  market: { toText(): string };
  receipt: { revision: bigint; paid_at: bigint };
}

export function newestProfiles<T extends PublishedProfile>(profiles: readonly T[]): T[] {
  return [...profiles].sort((a, b) => {
    if (a.receipt.paid_at !== b.receipt.paid_at) return a.receipt.paid_at > b.receipt.paid_at ? -1 : 1;
    return a.market.toText().localeCompare(b.market.toText());
  });
}

/** Keep the two homepage tables exhaustive and disjoint, preserving their chosen market ordering. */
export function partitionScreened<T extends { market: { toText(): string } }>(tokens: readonly T[], profiles: readonly PublishedProfile[]) {
  const published = new Set(profiles.filter(profile => profile.receipt.revision > 0n).map(profile => profile.market.toText()));
  return {
    screened: tokens.filter(token => published.has(token.market.toText())),
    other: tokens.filter(token => !published.has(token.market.toText())),
  };
}

/** The canister caps each page. Never classify tokens from an incomplete profile scan. */
export async function fetchCommunityProfiles<T extends PublishedProfile>(
  read: (offset: bigint, limit: bigint) => Promise<{ total: bigint; items: T[] }>,
): Promise<T[]> {
  const first = await read(0n, 100n);
  const profiles = [...first.items];
  const seen = new Set(profiles.map(profile => profile.market.toText()));
  if (first.total < 0n || BigInt(profiles.length) > first.total || seen.size !== profiles.length) throw Error('The community profile feed changed while loading. Please refresh.');
  while (BigInt(profiles.length) < first.total) {
    const page = await read(BigInt(profiles.length), 100n);
    if (page.total !== first.total || !page.items.length) throw Error('The community profile feed is incomplete. Please refresh.');
    for (const profile of page.items) {
      const id = profile.market.toText();
      if (seen.has(id)) throw Error('The community profile feed changed while loading. Please refresh.');
      seen.add(id);
      profiles.push(profile);
    }
    if (BigInt(profiles.length) > first.total) throw Error('The community profile feed is incomplete. Please refresh.');
  }
  if (profiles.some(profile => profile.receipt.revision <= 0n)) throw Error('The community profile feed contains an unconfirmed profile.');
  // A new publication moves to the first position. Recheck it after multi-page scans
  // so a concurrently updated profile cannot silently be omitted by offset paging.
  if (first.items.length && BigInt(first.items.length) < first.total) {
    const head = await read(0n, 1n);
    const before = first.items[0], after = head.items[0];
    if (head.total !== first.total || !after || before.market.toText() !== after.market.toText() || before.receipt.revision !== after.receipt.revision || before.receipt.paid_at !== after.receipt.paid_at) throw Error('The community profile feed changed while loading. Please refresh.');
  }
  return newestProfiles(profiles);
}
